import { configureStore } from "@reduxjs/toolkit";
import router from "../components/Routers/Router";
import { combineReducers } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import { createTransform, persistReducer, persistStore } from "redux-persist";
import storage from "redux-persist/lib/storage";
import addressBookSliceReducer from "../components/AddressBook/AddressBookSlice";
import GroupDataSliceReducer from "../components/AddressBook/GroupDataSlice";
import AlertSlice from "../components/Alert/AlertSlice";
import authSliceReducer from "../components/Auth/AuthSlice";
import BSFMSlice from "../components/BSFM/BSFMSlice";
import dashboardReducer from "../components/Dashboard/DashboardSlice";
import DltSlice from "../components/DLT/DltSlice";
import DownloadsSliceReducer from "../components/Downloads/DownloadsSlice";
import AutoScheduleSlice from "../components/Message/AutoSchedule/AutoScheduleSlice";
import BulkSmsSliceReducer from "../components/Message/BulkSms/BulkSmsSlice";
import BulkBySenderSlice from "../components/Message/BulkBySender/BulkBySenderSlice";
import MessageSliceReducer from "../components/Message/MessageSlice";
import resendSlice from "../components/Message/Resend/ResendSlice";
import SingleSmsSliceReducer from "../components/Message/SingleSms/SingleSmsSlice";
import NetworkSlice from "../components/Network/NetworkSlice";
import ProfileSliceReducer from "../components/Profile/ProfileSlice";
import ReportSliceReducer from "../components/Report/ReportSlice";
import RoutingSlice from "../components/Routing/RoutingSlice";
import SaleSliceReducer from "../components/Sales/SalesSlice";
import SMSCSlice from "../components/SMSC/SMSCSlice";
import SubscriptionSliceReducer from "../components/Subscription/SubscriptionSlice";
import TemplateSliceReducer from "../components/Templates/TemplateSlice";
import TwoWaySliceReducer from "../components/TwoWay/TwoWaySlice";
import UpdatePasswordReducer from "../components/UpdatePassword/UpdatePasswordSlice";
import UserSlice from "../components/User/UserSlice";
import BulkEditSlice from "../components/Utility/Edit/Bulk/BulkEditSlice";
import ScheduleEditSlice from "../components/Utility/Edit/Schedule/ScheduleEditSlice";
import WalletReducer from "../components/Wallet/WalletSlice";
import downloadProgressReducer from "../Layouts/Navbar/DownloadProgress/DownloadLogSlice";
import NotificationReducer from "../Layouts/Navbar/Notifications/NotificationSlice";
import { clearAuthStorage, kindOf, showToast } from "../lib/utility";
import commonUtilitySlice from "./commonUtilitySlice";
import CompanySliceReducer from "../components/Company/CompanySlice";
import VendorSliceReducer from "../components/Vendor/VendorSlice";
import PCapSliceReducer from "../components/PCap/PCapSlice";
import BillingSliceReducer from "../components/Billing/BillingSlice";
import updateApiKeyReducer from "../components/UpdateApiKey/UpdateApiKeySlice";

const rootReducer = combineReducers({
  auth: authSliceReducer,
  message: MessageSliceReducer,
  sendSms: SingleSmsSliceReducer,
  sendBulk: BulkSmsSliceReducer,
  bulkBySender: BulkBySenderSlice,
  profile: ProfileSliceReducer,
  template: TemplateSliceReducer,
  addressBook: addressBookSliceReducer,
  Group: GroupDataSliceReducer,
  Downloads: DownloadsSliceReducer,
  twoway: TwoWaySliceReducer,
  report: ReportSliceReducer,
  subscription: SubscriptionSliceReducer,
  dashboard: dashboardReducer,
  wallet: WalletReducer,
  notification: NotificationReducer,
  updatePassword: UpdatePasswordReducer,
  updateApiKey: updateApiKeyReducer,
  scheduleEdit: ScheduleEditSlice,
  bulkEdit: BulkEditSlice,
  autoSchedule: AutoScheduleSlice,
  sales: SaleSliceReducer,
  bsfm: BSFMSlice,
  user: UserSlice,
  routing: RoutingSlice,
  smsc: SMSCSlice,
  alert: AlertSlice,
  dlt: DltSlice,
  network: NetworkSlice,
  resend: resendSlice,
  downloadProgress: downloadProgressReducer,
  commonUtility: commonUtilitySlice,
  company: CompanySliceReducer,
  vendor: VendorSliceReducer,
  pcap: PCapSliceReducer,
  billing: BillingSliceReducer,
});

// Request/UI flags must not survive reload — persisting isFetching:true
// during login left the Sign In button disabled after a mid-request refresh.
const AUTH_TRANSIENT_DEFAULTS = {
  isFetching: false,
  isLoginSuccess: false,
  isOTPSuccess: false,
  isAccessDeviceSuccess: false,
  isError: false,
  errorMessage: "",
  isFetchingNavOptions: false,
  isnavOptionsFetchSuccess: false,
  isnavOptionsFetchError: false,
  navOptionsFetchErrorMessage: "",
  isFetchingLandingInfo: false,
  isFetchingLandingInfoCreate: false,
  isSuccessCreateLandingInfo: false,
  isFetchingLoginPanelSave: false,
  isErrorUpdateLandingInfo: false,
  updateLandingInfoErrorMsg: "",
  isFetchingLandingInfoAll: false,
  isFetchingAbacSession: false,
};

const authPersistTransform = createTransform(
  (inboundState) => {
    if (!inboundState) return inboundState;
    const persisted = { ...inboundState };
    for (const key of Object.keys(AUTH_TRANSIENT_DEFAULTS)) {
      delete persisted[key];
    }
    return persisted;
  },
  (outboundState) => {
    if (!outboundState) return outboundState;
    return {
      ...outboundState,
      ...AUTH_TRANSIENT_DEFAULTS,
    };
  },
  { whitelist: ["auth"] }
);

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["auth", "subscription"],
  blacklist: [],
  transforms: [authPersistTransform],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
      // serializableCheck: {
      //   // ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      //   ignoredActions: [PERSIST, REGISTER, PURGE],
      // },
    }),
  devTools: process.env.NODE_ENV !== "production",
});

export const persistor = persistStore(store, composeWithDevTools());

export const firstString = (i, ...args) => {
  let retString;

  for (let i = 0; i < args.length; i++) {
    if (typeof args[i] === "string" && args[i] !== "") {
      retString = args[i];
      break;
    }
  }
  return retString;
};

async function checkInternetConnection() {
  return await fetch("https://www.google.com/", {
    method: "HEAD",
    mode: "no-cors",
  })
    .then(() => true)
    .catch(() => false);
}

export const isFunction = (cb) => kindOf(cb, "function");

export const commonMiddleware = async (action, options = {}) => {
  const publicRoute =
    window.location.pathname == "/login" || window.location.pathname == "/";
  const guestRoute =
    window.location.pathname == "/update-password" ||
    window.location.pathname == "/update-api-key";
  const messageTypeMap = {
    create: ["Created", "Creating"],
    update: ["Updated", "Updating"],
    delete: ["Deleted", "Deleting"],
    get: ["Get", "Getting"],
  };
  let { type, name, cb, error, callBacks, customMessage } = options;

  let status = (action.payload?.status ?? action.payload?.statusCode ?? action.payload?.data?.status ?? action.payload?.data?.data?.status ?? action.payload?.errorCode)?.toString();
  let message = firstString(
    0,
    action.payload,
    action.payload?.message,
    action.payload?.data,
    action.payload?.data?.message,
    customMessage
  );
  callBacks = callBacks === undefined ? {} : callBacks;

  if (action.payload?.message === "Download Canceled") {
    return;
  }

  if (
    action?.error &&
    !["404", "401", "503", "400", "409", "502", "429"].includes(status)
  ) {
    if (!(await checkInternetConnection())) {
      if (!globalThis.isOffline || Date.now() > +globalThis.isOffline + 15000) {
        showToast("Internet Disconnected", "warning");
        globalThis.isOffline = Date.now();
      }
      return;
    } else {
      if (action?.error?.message === "Netork Error") {
        showToast("Network Issue, Please Check Internet Connection", "warning");
      }
      globalThis.isOffline = false;
    }
  } else globalThis.isOffline = false;

  if (
    status === "401" ||
    (action.payload?.error === "Unauthorized" &&
      action.payload?.message?.includes("JWT"))
  ) {
    if (window.location.pathname != "/login" || window.location.pathname != "/")
      showToast(
        publicRoute
          ? message
          : guestRoute
            ? "Unauthorized"
            : "User Session Expired",
        "warning"
      );
    setTimeout(() => {
      clearAuthStorage();
      persistor.purge();
      globalThis.profileData = null;
      document.body.classList.remove("dark-mode");
      if (!publicRoute) router.navigate("/login");
      globalThis.profileData = null;
    }, 1000);

    return;
  }

  if (status === "503") {
    showToast(message ?? "Service is under maintenance", "warning");
    return;
  }

  if (status === "429") {
    showToast(message ?? "Too many requests. Please try again later.", "warning");
    return;
  }

  if (status === "400") {
    showToast(message ?? "Bad request", "warning");
    return;
  }

  if (status === "404" || action.payload?.errorCode === "NOT_FOUND" || action.payload?.error?.includes("Not Found")) {
    const isWarningApi = [
      "Message",
      "Template List",
      "Templates",
      "Campaign List",
      "Schedule Manager ",
      "Two Way List",
      "Subscription list",
      "Campaign Aborted Report",
      "Campaign Report",
      "Schedule Report",
      "Delivery Report",
      "Summary Delivery",
      "Batch Summary Report",
      "Delivery Advance Report",
      "Content Report",
      "Latency Report",
      "Consumption Report",
      "Lookup Report",
      "Address Book Groups",
      "Contact Groups",
      "Error Code List",
      "Submission Report",
      "SMSC List",
      "SMSC Connections List",
      "Coverage Report",
      "Performance User Report",
      "Performance Route",
      "TPS Report",
      "EDR Report",
      "Vendor Summary Report",
      "Sales",
      "Resellers",
      "User List",
      "Aggregator Report",
      "Delivery SMSC Report",
      "Profit Report",
      "Blocked Report",
      "Saved Filter",
      "Dashboard Data",
      "Account Summary",
      "Recent Activity",
      "Performance Data",
      "Traffic Data",
      "Profile",
      "profile"
    ].map(v => v.trim().toLowerCase()).includes(name?.trim().toLowerCase());
    const toastType = "warning";

    !callBacks.noToast &&
      showToast(message ?? "No data found", toastType);
    if (isFunction(callBacks.modal)) callBacks.modal();
    if (isFunction(callBacks.notFound)) callBacks.notFound();
    return;
  }

  if (status === "409") {
    !callBacks.noToast && showToast(message ?? "Duplicate entry", "warning");
    return;
  }

  if (["create", "update"].includes(type)) {
    if (message?.includes("Duplicate")) {
      showToast("Duplicate entry", "warning");
      return;
    }
  }

  if (action.error || (action.payload && action.payload?.error)) {
    const isWarningApi = [
      "Message",
      "Template List",
      "Templates",
      "Campaign List",
      "Schedule Manager ",
      "Two Way List",
      "Subscription list",
      "Campaign Aborted Report",
      "Campaign Report",
      "Schedule Report",
      "Delivery Report",
      "Summary Delivery",
      "Batch Summary Report",
      "Delivery Advance Report",
      "Content Report",
      "Latency Report",
      "Consumption Report",
      "Lookup Report",
      "Address Book Groups",
      "Contact Groups",
      "Error Code List",
      "Submission Report",
      "SMSC List",
      "SMSC Connections List",
      "Coverage Report",
      "Performance User Report",
      "Performance Route",
      "TPS Report",
      "EDR Report",
      "Vendor Summary Report",
      "Sales",
      "Resellers",
      "User List",
      "Aggregator Report",
      "Delivery SMSC Report",
      "Profit Report",
      "Blocked Report",
      "Saved Filter",
      "Dashboard Data",
      "Account Summary",
      "Recent Activity",
      "Performance Data",
      "Traffic Data",
      "Profile",
      "profile"
    ].map(v => v.trim().toLowerCase()).includes(name?.trim().toLowerCase());
    const is4xx = status?.startsWith("4");
    const toastType = (isWarningApi && is4xx) ? "warning" : "error";

    !callBacks.noToast &&
      showToast(
        message ??
        error ??
        "Something went wrong",
        toastType
      );
    if (isFunction(callBacks.modal)) callBacks.modal();
    return;
  }

  if (type !== "get") {
    !callBacks.noToast &&
      showToast(
        message ?? "Operation completed successfully",
        "success"
      );
    if (isFunction(callBacks.modal)) callBacks.modal();

    if (!callBacks.isLast) {
      setTimeout(() => {
        isFunction(callBacks.refresh) && callBacks.refresh();
      }, callBacks.refreshTime ?? 1200);
    }
  }

  isFunction(cb) && cb(action);
};
export const middleware = (
  action,
  name,
  cb,
  error,
  callBacks,
  customMessage
) => {
  commonMiddleware(action, {
    name,
    cb,
    error,
    callBacks,
    type: "get",
    customMessage,
  });
};
export const middlewareCreate = (action, name, cb, error, callBacks) => {
  commonMiddleware(action, {
    name,
    cb,
    error,
    callBacks,
    type: "create",
  });
};
export const middlewareUpdate = (action, name, cb, error, callBacks) => {
  commonMiddleware(action, {
    name,
    cb,
    error,
    callBacks,
    type: "update",
  });
};
export const middlewareDelete = (action, name, cb, error, callBacks) => {
  commonMiddleware(action, {
    name,
    cb,
    error,
    callBacks,
    type: "delete",
  });
};


